import React, { createContext, useState, ReactNode, useEffect, useContext } from 'react';

interface SettingsContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
  showFileInfos: boolean;
  toggleShowFileInfos: () => void;
  currency: string;
  setCurrency: (currency: string) => void;
  showCurrencySymbol: boolean;
  toggleShowCurrencySymbol: () => void;
  language: string;
  setLanguage: (language: string) => void;
  getLocale: () => string;
  getCurrencySymbol: () => string;
  collapsed: boolean;
  toggleCollapsed: () => void;
  showAdvancedMetrics: boolean;
  toggleShowAdvancedMetrics: () => void;
}

export const SettingsContext = createContext<SettingsContextType>({
  darkMode: false,
  toggleDarkMode: () => {},
  showFileInfos: false,
  toggleShowFileInfos: () => {},
  currency: 'USD',
  setCurrency: () => {},
  showCurrencySymbol: true,
  toggleShowCurrencySymbol: () => {},
  language: 'en',
  setLanguage: () => {},
  getLocale: () => 'en-US',
  getCurrencySymbol: () => 'USD',
  collapsed: false,
  toggleCollapsed: () => {},
  showAdvancedMetrics: false,
  toggleShowAdvancedMetrics: () => {},
});

export const useSettings = () => {
  return useContext(SettingsContext);
};

export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  const [showFileInfos, setShowFileInfos] = useState(false);
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('currency') || 'USD';
  });
  const [showCurrencySymbol, setShowCurrencySymbol] = useState(() => {
    return localStorage.getItem('showCurrencySymbol') === 'true';
  });
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });
  const [collapsed, setCollapsed] = useState(false);
  const [showAdvancedMetrics, setShowAdvancedMetrics] = useState<boolean>(false);

  useEffect(() => {
    localStorage.setItem('darkMode', darkMode.toString());
    // document.body.classList.toggle('dark-mode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('currency', currency);
  }, [currency]);

  useEffect(() => {
    localStorage.setItem('showCurrencySymbol', showCurrencySymbol.toString());
  }, [showCurrencySymbol]);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  useEffect(() => {
    const storedShowAdvancedMetrics = localStorage.getItem('showAdvancedMetrics');
    if (storedShowAdvancedMetrics) {
      setShowAdvancedMetrics(JSON.parse(storedShowAdvancedMetrics));
    }
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const toggleShowFileInfos = () => {
    setShowFileInfos(prev => !prev);
  };

  const toggleShowCurrencySymbol = () => {
    setShowCurrencySymbol(prev => !prev);
  };

  const toggleCollapsed = () => {
    setCollapsed(prev => !prev);
  };

  const toggleShowAdvancedMetrics = () => {
    setShowAdvancedMetrics(prev => {
      const newValue = !prev;
      localStorage.setItem('showAdvancedMetrics', JSON.stringify(newValue));
      return newValue;
    });
  };

  const getLocale = () => {
    switch (language) {
      case 'de':
        return 'de-DE';
      case 'fr':
        return 'fr-FR';
      // Add more cases as needed
      default:
        return 'en-US';
    }
  };

  const getCurrencySymbol = () => {
    return currency;
  };

  return (
    <SettingsContext.Provider value={{ darkMode, toggleDarkMode, showFileInfos, toggleShowFileInfos, currency, setCurrency, showCurrencySymbol, toggleShowCurrencySymbol, language, setLanguage, getLocale, getCurrencySymbol, collapsed, toggleCollapsed, showAdvancedMetrics, toggleShowAdvancedMetrics }}>
      {children}
    </SettingsContext.Provider>
  );
};